import { currentTheme as currentVizTheme, refreshTheme } from 'devextreme/viz/themes';
import { current as getCurrentDXTheme } from 'devextreme/ui/themes';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const themes = ['light', 'dark'] ;
const storageKey = 'app-theme';
const themePrefix = 'app-theme-';

const prefixes = ['./styles/theme-dx-', './styles/variables-'];

const loadStylesImports = async() => {
  // Check if DevExtreme themes are already loaded via HTML
  const dxThemeLoaded = getComputedStyle(document.body).getPropertyValue('--dx-theme-loaded');
  
  if (dxThemeLoaded) {
    // DevExtreme themes are already loaded, only load app-specific SCSS
    await Promise.all([
      ...prefixes.flatMap((prefix) => [
        import(/* webpackChunkName: "app-theme-dark" */ `${prefix}dark.scss`),
        import(/* webpackChunkName: "app-theme-light" */ `${prefix}light.scss`)
      ]),
    ]);
  } else {
    // Fallback: load all themes including DevExtreme
    await Promise.all([
      ...prefixes.flatMap((prefix) => [
        import(/* webpackChunkName: "app-theme-dark" */ `${prefix}dark.scss`),
        import(/* webpackChunkName: "app-theme-light" */ `${prefix}light.scss`)
      ]),
    ]);
  }
};


function getNextTheme(theme) {
  return themes[themes.indexOf(theme) + 1] || themes[0];
}

function getCurrentTheme() {
  return window.localStorage[storageKey] || getNextTheme();
}

function isThemeStyleSheet(styleSheet, theme) {
  const themeMarker = `${themePrefix}${theme}`;
  // eslint-disable-next-line no-undef
  if(process.env.NODE_ENV === 'production') {
    return styleSheet?.href?.includes(`${themeMarker}`);
  } else {
    const rules = Array.from(styleSheet.cssRules);
    return !!rules.find((rule) => rule?.selectorText?.includes(`.${themeMarker}`));
  }
}

function switchThemeStyleSheets(enabledTheme) {
  const disabledTheme = getNextTheme(enabledTheme);

  Array.from(document.styleSheets).forEach((styleSheet) => {
    styleSheet.disabled = isThemeStyleSheet(styleSheet, disabledTheme);
  });
}

async function setAppTheme(newTheme) {
  const themeName = newTheme || getCurrentTheme();

  switchThemeStyleSheets(themeName);

  const regexTheme = new RegExp(`\\.(${themes.join('|')})`, 'g');
  currentVizTheme(currentVizTheme().replace(regexTheme, `.${themeName}`));
  console.log(`Theme changed to: ` + currentVizTheme().replace(regexTheme, `.${themeName}`));
  refreshTheme();
}

function toggleTheme(currentTheme) {
  const newTheme = getNextTheme(currentTheme);
  window.localStorage[storageKey] = newTheme;
  return newTheme;
}

export function useThemeContext() {
  const [theme, setTheme] = useState(getCurrentTheme());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check if DevExtreme themes are already loaded via HTML
    const dxThemeLoaded = getComputedStyle(document.body).getPropertyValue('--dx-theme-loaded');
    
    if (dxThemeLoaded && dxThemeLoaded.trim() === '1') {
      // DevExtreme themes are preloaded, faster loading
      const fastLoad = () => {
        loadStylesImports()
          .then(() => {
            setIsLoaded(true);
            const rootElement = document.getElementById('root');
            if (rootElement) {
              rootElement.classList.add('theme-loaded');
            }
          })
          .catch((error) => {
            console.error('Theme loading error:', error);
            // Still set as loaded to prevent blocking
            setIsLoaded(true);
            const rootElement = document.getElementById('root');
            if (rootElement) {
              rootElement.classList.add('theme-loaded');
            }
          });
      };
      
      fastLoad();
    } else {
      // Fallback with timeout for iOS compatibility
      const timeoutId = setTimeout(() => {
        console.warn('Theme loading timeout - setting as loaded to prevent iOS issues');
        setIsLoaded(true);
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.classList.add('theme-loaded');
        }
      }, 2000); // 2 second timeout

      loadStylesImports()
        .then(() => {
          clearTimeout(timeoutId);
          setIsLoaded(true);
          const rootElement = document.getElementById('root');
          if (rootElement) {
            rootElement.classList.add('theme-loaded');
          }
        })
        .catch((error) => {
          console.error('Theme loading error:', error);
          clearTimeout(timeoutId);
          // Still set as loaded to prevent blocking
          setIsLoaded(true);
          const rootElement = document.getElementById('root');
          if (rootElement) {
            rootElement.classList.add('theme-loaded');
          }
        });
    }
  }, []);

  const switchTheme = useCallback(() => setTheme((currentTheme) => toggleTheme(currentTheme)), []);

  const isFluent = useCallback(() => {
    return getCurrentDXTheme().includes('fluent');
  }, []);

  useEffect(() => {
    isLoaded && setAppTheme(theme);
  }, [theme, isLoaded]);

  return useMemo(()=> ({ theme, switchTheme, isLoaded, isFluent }), [theme, isLoaded, isFluent]);
}

export const ThemeContext = React.createContext(null);
